// Portfolio API helpers — både authenticated CRUD och publika by-slug.

import { api, ApiError } from './client';

interface DataEnvelope<T> { data: T; }

// ─── Types ───────────────────────────────────────────────────────────────

export type PortfolioAccessMode = 'public' | 'password' | 'expiring';

export interface PortfolioHeaderData {
  modelName?: string;
  /** ISO date (YYYY-MM-DD). Age beräknas automatiskt vid render. */
  birthday?: string;
  /** Bas-stad / land, t.ex. "Barcelona, Spain". */
  location?: string;
  /** Etnicitet / bakgrund, t.ex. "Swedish/Romanian". */
  ethnicity?: string;
  measurements?: {
    height?: string;
    bust?: string;
    waist?: string;
    hips?: string;
    /** Skostorlek, t.ex. "EU 38". */
    shoeSize?: string;
    hair?: string;
    eyes?: string;
  };
  contact?: {
    email?: string;
    phone?: string;
    instagram?: string;
  };
  intro?: string;
  brand?: string;
}

export interface PortfolioSummary {
  id: string;
  workspaceId: string;
  name: string;
  slug: string;
  primarySetId: string;
  secondarySetId: string | null;
  headerData: PortfolioHeaderData | null;
  accessMode: PortfolioAccessMode;
  expiresAt: string | null;
  hasPin: boolean;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  lastViewedAt: string | null;
}

export interface PublicPortfolioImage {
  id: string;
  filename: string;
  width: number | null;
  height: number | null;
  sortOrder: number;
  note: string | null;
  shootId: string;
  shootName: string;
  previewUrl: string;
  fullUrl: string;
}

export interface PublicPortfolioSet {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  imageCount: number;
  images: PublicPortfolioImage[];
}

export interface PublicPortfolio extends PortfolioSummary {
  primarySet: PublicPortfolioSet;
  secondarySet?: PublicPortfolioSet;
  /** True om besökaren är inloggad workspace-member — frontend visar då
   *  edit-tools (drag-and-drop, etc). */
  isOwner: boolean;
}

// För locked-state — bara name + accessMode visas
export interface LockedPortfolio {
  id: string;
  name: string;
  accessMode: PortfolioAccessMode;
  expiresAt?: string;
}

export type PublicPortfolioResponse =
  | { kind: 'unlocked'; portfolio: PublicPortfolio }
  | { kind: 'locked'; portfolio: LockedPortfolio }
  | { kind: 'expired'; portfolio: LockedPortfolio }
  | { kind: 'not-found' };

// ─── Authenticated CRUD ──────────────────────────────────────────────────

export async function listPortfolios(workspaceId: string): Promise<PortfolioSummary[]> {
  const res = await api.get<DataEnvelope<PortfolioSummary[]>>(
    `/workspaces/${workspaceId}/portfolios`,
  );
  return res.data;
}

export interface CreatePortfolioPayload {
  name: string;
  primarySetId: string;
  secondarySetId?: string;
  slug?: string;
  headerData?: PortfolioHeaderData;
  accessMode?: PortfolioAccessMode;
  pin?: string;
  expiresAt?: string;
}

export async function createPortfolio(
  workspaceId: string,
  payload: CreatePortfolioPayload,
): Promise<PortfolioSummary> {
  const res = await api.post<DataEnvelope<PortfolioSummary>>(
    `/workspaces/${workspaceId}/portfolios`,
    payload,
  );
  return res.data;
}

export async function getPortfolio(id: string): Promise<PortfolioSummary> {
  const res = await api.get<DataEnvelope<PortfolioSummary>>(`/portfolios/${id}`);
  return res.data;
}

export interface UpdatePortfolioPayload {
  name?: string;
  primarySetId?: string;
  secondarySetId?: string | null;
  headerData?: PortfolioHeaderData | null;
  accessMode?: PortfolioAccessMode;
  pin?: string | null;
  expiresAt?: string | null;
}

export async function updatePortfolio(
  id: string,
  payload: UpdatePortfolioPayload,
): Promise<PortfolioSummary> {
  const res = await api.patch<DataEnvelope<PortfolioSummary>>(
    `/portfolios/${id}`,
    payload,
  );
  return res.data;
}

export async function deletePortfolio(id: string): Promise<void> {
  await api.delete<void>(`/portfolios/${id}`);
}

export interface PortfolioStats {
  totalViews: number;
  totalUnlockAttempts: number;
  totalUnlockSuccess: number;
  last7Days: Array<{ day: string; views: number }>;
}

export async function getPortfolioStats(id: string): Promise<PortfolioStats> {
  const res = await api.get<DataEnvelope<PortfolioStats>>(`/portfolios/${id}/stats`);
  return res.data;
}

// ─── Full stats (Umami-proxy) ────────────────────────────────────────────

export type StatsRange = '7d' | '30d' | 'all';

export interface PortfolioStatsFull {
  range: StatsRange;
  rangeMillis: { startAt: number; endAt: number };
  totals: {
    totalViews: number;
    uniqueVisitors: number;
    avgViewsPerVisitor: number;
    scrollDepthCounts: Record<'0.25' | '0.5' | '0.75' | '1', number>;
  };
  topImages: Array<{ imageId: string; views: number }>;
  contactClicks: { email: number; phone: number; instagram: number };
  dailySeries: Array<{ day: string; views: number }>;
}

export async function getPortfolioStatsFull(
  id: string,
  range: StatsRange,
): Promise<PortfolioStatsFull> {
  const res = await api.get<DataEnvelope<PortfolioStatsFull>>(
    `/portfolios/${id}/stats/full`,
    { query: { range } },
  );
  return res.data;
}

export async function resetPortfolioViews(id: string): Promise<{ deleted: number; resetAt: string }> {
  const res = await api.delete<DataEnvelope<{ deleted: number; resetAt: string }>>(
    `/portfolios/${id}/views`,
  );
  return res.data;
}

// ─── Public (by-slug) ────────────────────────────────────────────────────

/**
 * Hämta publik portfolio. Returnerar discriminated union baserat på status:
 *   - 'unlocked' med full data
 *   - 'locked' om PIN krävs (klient ska visa unlock-prompt)
 *   - 'expired' om expiring + utgången
 *   - 'not-found'
 */
export async function getPublicPortfolio(slug: string): Promise<PublicPortfolioResponse> {
  try {
    const res = await api.get<DataEnvelope<PublicPortfolio>>(
      `/portfolios/by-slug/${encodeURIComponent(slug)}`,
    );
    return { kind: 'unlocked', portfolio: res.data };
  } catch (err) {
    if (err instanceof ApiError) {
      // 401 PORTFOLIO_LOCKED — klient ska visa unlock-prompt
      if (err.status === 401 && err.code === 'PORTFOLIO_LOCKED') {
        const body = err.body as { data?: { portfolio: LockedPortfolio } } | undefined;
        if (body?.data?.portfolio) {
          return { kind: 'locked', portfolio: body.data.portfolio };
        }
      }
      // 410 PORTFOLIO_EXPIRED
      if (err.status === 410 && err.code === 'PORTFOLIO_EXPIRED') {
        const body = err.body as { data?: { portfolio: LockedPortfolio } } | undefined;
        if (body?.data?.portfolio) {
          return { kind: 'expired', portfolio: body.data.portfolio };
        }
      }
      if (err.status === 404) {
        return { kind: 'not-found' };
      }
    }
    throw err;
  }
}

/**
 * Lås upp en password-protected portfolio med PIN. Vid success sätts
 * unlock-cookie automatiskt — nästa getPublicPortfolio-call returnerar
 * full data.
 */
export async function unlockPortfolio(
  slug: string,
  pin: string,
): Promise<{ unlocked: boolean }> {
  const res = await api.post<DataEnvelope<{ unlocked: boolean }>>(
    `/portfolios/by-slug/${encodeURIComponent(slug)}/unlock`,
    { pin },
  );
  return res.data;
}
